"use client"

import { useState, type ReactNode } from "react"
import { Form, Formik } from "formik"
import { useLocale, useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { FormSelect } from "@/components/form-inputs/form-select"
import { useUpdateMyUiLocale } from "@/hooks/mutations/ui-locale"
import { RELEASED_UI_LOCALES, UI_LOCALE_OPTIONS, type UiLocale } from "@/lib/i18n/locales"

type UiLanguageFormProps = {
  onSaveProfile?: (locale?: UiLocale) => Promise<boolean>
  timezoneField?: ReactNode
  children?: (isSubmitting: boolean) => ReactNode
}

export function UiLanguageForm({ onSaveProfile, children, timezoneField }: UiLanguageFormProps) {
  const locale = useLocale()
  const t = useTranslations("UserProfile")
  const common = useTranslations("Common")
  const mutation = useUpdateMyUiLocale()
  const [savedLocale, setSavedLocale] = useState<UiLocale | null>(null)
  const hasLanguageOptions = RELEASED_UI_LOCALES.length >= 2

  return (
    <Formik initialValues={{ locale }} enableReinitialize onSubmit={async (values, { setErrors }) => {
      if (onSaveProfile) {
        await onSaveProfile(values.locale === locale ? undefined : values.locale)
        return
      }
      if (!hasLanguageOptions) return
      try {
        await mutation.mutateAsync({ ...values, setErrors })
        setSavedLocale(values.locale)
      } catch {
        // The mutation presents errors and Formik restores the submit button for retry.
      }
    }}>
      {({ isSubmitting }) => <Form className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {hasLanguageOptions && <div>
          <FormSelect name="locale" label={t("languageLabel")} options={UI_LOCALE_OPTIONS} disabled={isSubmitting} />
        </div>}
        {timezoneField}
        </div>
        {savedLocale === locale ? <p role="status" className="text-sm text-green-700 dark:text-green-400"><span key={t("languageSaved")}>{t("languageSaved")}</span></p> : null}
        {children ? children(isSubmitting) : <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            <span key={[isSubmitting ? "saving" : "save", isSubmitting ? common("saving") : common("save")].join(":")}>
              {isSubmitting ? common("saving") : common("save")}
            </span>
          </Button>
        </div>}
      </Form>}
    </Formik>
  )
}
